import ApolloClient from "apollo-client";
import { withClientState } from "apollo-link-state";
import { InMemoryCache } from "apollo-cache-inmemory";
import { ApolloLink } from "apollo-link";
import { setContext } from "apollo-link-context";
import { onError } from "apollo-link-error";
import { RetryLink } from "apollo-link-retry";
import { BatchHttpLink } from "apollo-link-batch-http";
import omitDeep from "omit-deep-lodash";
import { storage, getDeviceLocale } from "cci-utility";
import { getSessionInformation } from "cci-data";
import getConnectionInfoQuery from "../query/GetConnectionInfoQuery";
import {
  login,
  logout,
  forgotPassword,
  resetPassword,
  isMfaEnabled
} from "../query/Authentication";

/**
 * Cross-site Request Forging token, cached for quick access.
 *
 * This is usually stored in local storage, we have it in a local variable for quick access.
 */
let csrfToken;

/**
 * Apollo Cache so that the server is not constantly queried for things we already have in the client side.
 */
const cache = new InMemoryCache();

const getApolloCache = () => cache;

const getApolloClient = () => client;
/**
 * Apollo Client instance, so we can use the writeData method in setConnectionInfo.
 */
let client;

/**
 * Directly query the Apollo cache to obtain connection info since it is not on the server.
 */
const getConnectionInfo = () => {
  try {
    return cache.readQuery({
      query: getConnectionInfoQuery,
      errorPolicy: "all"
    }).ConnectionInfo;
  } catch (e) {
    return {};
  }
};

/**
 * Sets the connection info directly in the Apollo cache since it does not reside on the server.
 *
 * Here we store information such as the session id, CSRF and tokens, etc.
 */
const setConnectionInfo = info => {
  // Do we really need to read the previous info when we use writeData directly? If not, we can delete this function.
  const prev = getConnectionInfo();

  // We cannot use Cache's writeData because it does not seem to trigger a re-render.
  client.writeData({
    data: {
      ConnectionInfo: {
        ...prev,
        ...info,
        __typename: "ConnectionInfo"
      }
    }
  });
};

/**
 * Stores the CSRF token in local storage (in case the page is refreshed) and in a local variable for faster access.
 */
const storeCsrfToken = token => {
  storage.save({ key: "s", data: token });
  csrfToken = token;
};

/**
 * This is a variation of https://github.com/apollographql/apollo-client/issues/1564#issuecomment-347908532
 * Which removes the "__typename" (that Apollo adds to the cache) and "__new" (which FormToManyField adds) before the request is sent to the server.
 */
const cleanKeysLink = new ApolloLink((operation, forward) => {
  const newOperation = operation;

  if (newOperation.variables) {
    newOperation.variables = omitDeep(
      newOperation.variables,
      "__typename",
      "__new"
    );
  }

  return forward(newOperation);
});

/**
 * Caches the authentication token:
 * https://github.com/apollographql/apollo-link/tree/5fbaaf47e5ff1189d65ee674f09fbda464c23d5a/packages/apollo-link-context#caching-lookups
 *
 * We use this local cache to take into consideration the following scenario:
 * 1. User logs in
 * 2. Server sends the CSRF token
 * 3. We store it locally in our cookie and csrfToken cache
 * 4. Time passes by and the user's session times out on the server
 * 5. User comes back and makes another request on the same page.
 * 6. Server uses remember-me token, creates a new session and sends the new CSRF token
 * 7. Client receives the new token, saves it to the cookie and local cache
 * 8. Client retries the request with the new token (which is now pulled from the local cache)
 */

// Send the Cross Site Request Forging token in the HTTP headers, so that the server approves the requests.
const authLink = setContext(
  (request, previousContext) =>
    new Promise((success, fail) => {
      if (csrfToken !== undefined) {
        // We already have a token, no need to pull it from storage
        success();
      } else if (storage) {
        storage
          .load({
            key: "s",
            autoSync: false,
            syncInBackground: false
          })
          .then(token => {
            // Cache our token for subsequent requests
            csrfToken = token;

            // Added header to first request
            success({
              headers: {
                ...previousContext.headers,
                "X-CSRF-TOKEN": token
              }
            });
          })
          .catch(error => {
            if (error.name === "NotFoundError") {
              /*
               * If the error occurred is that the key is not found, then we don't set the headers and let
               * Apollo continue as the problem will be caught by the error link below.
               */
              success();
            } else {
              fail(new Error(error));
            }
          });
      } else {
        setConnectionInfo({
          errorMessage:
            "Cannot store local data in your browser. This may be due to you running in Private mode, please turn it off in order to use the app."
        });
      }
    })
);

// Update the server indicator based on the request response (network or server errors)
const errorLink = onError(({ graphQLErrors, networkError }) => {
  // Server connection is "lost" when a network error has occurred but the status code is not defined (connection refused).
  const connectionLost =
    networkError !== undefined && networkError.response === undefined;
  let sessionInformation = {};

  try {
    sessionInformation = cache.readQuery({
      query: getSessionInformation,
      errorPolicy: "all"
    });
  } catch (e) {
    sessionInformation = {};
  }

  setConnectionInfo({
    connectionLost,

    // Server sent an accessDenied error so we turn our flag on.
    authorizationFailed:
      // Server returned HTTP status code 403
      (networkError !== undefined &&
        networkError.response !== undefined &&
        networkError.response.status === 403) ||
      // Server returned HTTP status code 200 but the graphQLErrors array has the "accessDenied" message
      (graphQLErrors !== undefined &&
        graphQLErrors.length > 0 &&
        graphQLErrors[0].message === "accessDenied"),

    // Server sent a 401 code which means that the user has not entered their user id and password
    authenticated:
      cache.offlineSupport === true && connectionLost === true
        ? // Connection is lost and this app has offline support so do a session information check as "offline authentication"
          sessionInformation !== undefined &&
          sessionInformation.sessionInformation !== undefined &&
          sessionInformation.sessionInformation.user !== undefined &&
          sessionInformation.sessionInformation.user !== null
        : // Server sent a 401 code which means that the user has not entered their user id and password
          !(
            networkError !== undefined &&
            networkError.response !== undefined &&
                                                   // Forbidden
            (networkError.response.status === 401 || networkError.response.status === 403)
          )
  });
});

// Set up the "retrier" for when the session has timed out and a new CSRF token has been sent
const reauthorizeLink = new RetryLink({
  attempts: {
    max: 2,

    /*
     * Session timed out and the server sent us a new CSRF token.
     *
     * We simply save the token in the cookie and our local cache so that it can be included in the next request headers.
     */
    retryIf: error => {
      if (error.result && error.result.token && storage) {
        storeCsrfToken(error.result.token);

        return true;
      }

      return false;
    }
  }
});

// Set up the "retrier" when the server is reportedly down.
const reconnectServerLink = new RetryLink({
  attempts: {
    max: Infinity,

    /*
     * We used the response === undefined check to determine if the connection is refused or if the server can't be reached.
     *
     * This is the only case where we must retry the request indefinitely.
     */
    retryIf: error => error.response === undefined
  }
});

const initApollo = config => {
  // Not thrilled with this solution, but the cache gets added to the context and some of our links need the appUrl
  cache.appUrl = config.appUrl;
  cache.offlineSupport = config.offlineSupport || false;

  /**
   * Establishes the endpoint for the GraphQL server so that remote queries and mutations are executed.
   */
  const serverLink = new BatchHttpLink({
    uri: `${config.appUrl}/bgraphql`,
    credentials: "include",
    fetch: (uri, options) => {
      const newOptions = options;

      if (csrfToken) {
        /**
         * Add our local CSRF token to the request if there is one
         *
         * This is VERY important to have because it handles session time outs.
         */
        newOptions.headers["X-CSRF-TOKEN"] = csrfToken;
      }

      return fetch(uri, newOptions);
    },
    batchMax: 1000,

    /*
     * Amount of time to wait to batch requests together (in milliseconds).
     * This number should NOT be too high as it will make the app appear slow.
     */
    batchInterval: 150
  });
  //  const serverLink = new HttpLink({
  //    uri: `${config.appUrl}/graphql`,
  //    credentials: "include",
  //    fetch: (uri, options) => {
  //      const newOptions = options;
  //
  //      if (csrfToken) {
  //        /**
  //         * Add our local CSRF token to the request if there is one
  //         *
  //         * This is VERY important to have because it handles session time outs.
  //         */
  //        newOptions.headers["X-CSRF-TOKEN"] = csrfToken;
  //      }
  //
  //      return fetch(uri, newOptions);
  //    }
  //  });

  const stateLink = withClientState({
    cache,
    resolvers: {
      ...config.resolvers,

      // Our default mutations and queries go here, all apps need these
      Mutation: {
        login,
        logout,
        forgotPassword,
        isMfaEnabled,
        resetPassword,
        ...config.mutationResolvers
      }
    },
    defaults: {
      ConnectionInfo: {
        __typename: "ConnectionInfo",
        appUrl: config.appUrl,
        imageResizerUrl: config.imageResizerUrl,
        locale: getDeviceLocale(),
        authorizationFailed: false,
        authenticated: false,
        errorMessage: null,
        connectionLost: false,
        menuVisible: false
      },

      ...config.defaults
    }
  });

  // Initialize an object with the links that we support so that the linksModifier (if specified) can alter the sequence if necessary
  const links = {
    stateLink,
    cleanKeysLink,
    authLink,
    reauthorizeLink,
    reconnectServerLink,
    errorLink,
    serverLink
  };

  // Invokes the link modifier if one was specified
  const modifiedLinks = config.linksModifier
    ? config.linksModifier({ links })
    : Object.values(links);

  client = new ApolloClient({
    link: ApolloLink.from(modifiedLinks),
    cache,

    // This is needed to enable @client support as per: https://stackoverflow.com/a/55970913/3669247
    // resolvers: {}
  });

  // Ensure that the defaults are re-written when Apollo is reset (when logging out)
  client.onResetStore(stateLink.writeDefaults);

  return client;
};

export {
  initApollo,
  setConnectionInfo,
  storeCsrfToken,
  getApolloCache,
  getApolloClient
};
